import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

export default class extends Controller {
  static targets = ["list", "template"]

  connect() {
    if (!this.hasListTarget) return

    this.sortable = Sortable.create(this.listTarget, {
      handle: ".slide-drag-handle",
      animation: 150,
      onEnd: () => this.renumberPositions()
    })

    this.listTarget.querySelectorAll("[data-slide-row]").forEach((row) => {
      const isCollapsed = row.dataset.slideCollapsed === "true"
      this.setCollapsed(row, isCollapsed)
    })

    this.syncAllSlideTitles()
    this.renumberPositions()
  }

  disconnect() {
    if (this.sortable) {
      this.sortable.destroy()
      this.sortable = null
    }
  }

  addSlide() {
    if (!this.hasTemplateTarget || !this.hasListTarget) return

    const index = this.listTarget.querySelectorAll("[data-slide-row]").length
    const html = this.templateTarget.innerHTML.replace(/NEW_INDEX/g, String(index))
    this.listTarget.insertAdjacentHTML("beforeend", html)
    const row = this.listTarget.querySelector("[data-slide-row]:last-child")
    if (row) {
      this.setCollapsed(row, true)
      this.syncSlideTitle(row)
    }
    this.renumberPositions()
  }

  removeSlide(event) {
    const row = event.target.closest("[data-slide-row]")
    if (!row) return

    const destroyField = row.querySelector("[data-slide-destroy]")
    if (destroyField) {
      this.setCollapsed(row, true)
      destroyField.value = "1"
      row.classList.add("hidden")
    } else {
      row.remove()
    }

    this.renumberPositions()
  }

  toggleSlide(event) {
    const row = event.target.closest("[data-slide-row]")
    if (!row) return

    const isCollapsed = row.dataset.slideCollapsed === "true"
    this.setCollapsed(row, !isCollapsed)
  }

  updateSlideTitle(event) {
    const row = event.target.closest("[data-slide-row]")
    if (!row) return

    this.syncSlideTitle(row)
  }

  renumberPositions() {
    if (!this.hasListTarget) return

    const rows = this.listTarget.querySelectorAll("[data-slide-row]:not(.hidden)")
    rows.forEach((row, index) => {
      const positionField = row.querySelector("[data-slide-position]")
      if (positionField) positionField.value = String(index)
    })
  }

  setCollapsed(row, collapsed) {
    const content = row.querySelector("[data-slide-content]")
    if (content) content.classList.toggle("hidden", collapsed)

    row.dataset.slideCollapsed = collapsed ? "true" : "false"

    if (!collapsed) this.initializePendingEditors(row)

    const titleInput = row.querySelector("[data-slide-title-input]")
    if (titleInput) titleInput.required = !collapsed

    const icon = row.querySelector("[data-slide-toggle-icon]")
    if (icon) {
      icon.classList.toggle("icon-[lucide--chevrons-down]", collapsed)
      icon.classList.toggle("icon-[lucide--chevrons-up]", !collapsed)
    }

    const toggleButton = row.querySelector("[data-action~='welcome-screen-slides#toggleSlide']")
    if (toggleButton) toggleButton.setAttribute("aria-label", collapsed ? "Expand slide" : "Collapse slide")
  }

  syncAllSlideTitles() {
    if (!this.hasListTarget) return

    const rows = this.listTarget.querySelectorAll("[data-slide-row]")
    rows.forEach((row) => this.syncSlideTitle(row))
  }

  syncSlideTitle(row) {
    const titleInput = row.querySelector("[data-slide-title-input]")
    const titleDisplay = row.querySelector("[data-slide-title-display]")
    if (!titleDisplay) return

    const title = titleInput?.value?.trim()
    titleDisplay.textContent = title || "Untitled Slide"
  }

  initializePendingEditors(row) {
    row.querySelectorAll("[data-editorjs-pending]").forEach((container) => {
      container.removeAttribute("data-editorjs-pending")
      container.setAttribute("data-controller", "editorjs")
    })
  }
}
